import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Tooltip,
} from 'antd'
import { FormInstance } from 'antd/lib/form'
import moment, { Moment } from 'moment'
import {
  GET_REPORT_FIELD_CONFIGS,
  ReportFieldConfigVO,
  ReportFieldConfigResult,
  ReportFieldValueSubmitDTO,
} from '@/api/reportApi'

interface Props {
  platformCode?: string
  readOnly?: boolean
  reportingType?: string
  form: FormInstance
  onConfigChange?: (fields: ReportFieldConfigVO[]) => void
  /** 物料详情/提报详情接口返回的已保存字段值，用于回显 */
  reportFieldValues?: ReportFieldValueSubmitDTO[]
}

const formItemLayout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
}

const controlTypeMap = {
  NUMBER: '1',
  TEXT: '2',
  AMOUNT: '3',
  TIME: '4',
  SINGLE_SELECT: '5',
  MULTI_SELECT: '6',
} as const

type ValidationOperator = '>' | '>=' | '<' | '<=' | '='
type ValidationRule = { operator?: ValidationOperator; value?: string }

/** 根据 validationRules 拼默认提示：有下界才出现「大于…」，有上界才出现「小于…」；仅有 = 等其它规则时返回 null */
const formatValidationRangeHint = (rules: ValidationRule[]) => {
  const lowerParts: string[] = []
  const upperParts: string[] = []
  for (const rule of rules) {
    const v = Number(rule.value)
    if (Number.isNaN(v) || !rule.operator) continue
    if (rule.operator === '>') lowerParts.push(`大于${v}`)
    else if (rule.operator === '>=') lowerParts.push(`大于等于${v}`)
    else if (rule.operator === '<') upperParts.push(`小于${v}`)
    else if (rule.operator === '<=') upperParts.push(`小于等于${v}`)
  }
  if (!lowerParts.length && !upperParts.length) return null
  const minText = lowerParts.length ? lowerParts.join('、') : ''
  const maxText = upperParts.length ? upperParts.join('、') : ''
  if (minText && maxText) return `输入请符合${minText}，${maxText}`
  if (minText) return `输入请符合${minText}`
  if (maxText) return `输入请符合${maxText}`
  return null
}
type NumberRule = {
  numberType?: 'integer' | 'decimal'
  decimalDigits?: number
  validationRules?: ValidationRule[]
}
type TextRule = {
  charTypes?: Array<'中文' | '英文' | '数字' | '符号'>
  minLength?: number
  maxLength?: number
}
type AmountRule = { decimalDigits?: number; validationRules?: ValidationRule[] }
type TimeRule = {
  format?: 'YYYY' | 'YYYY-MM' | 'YYYY-MM-DD'
  startTime?: string
  endTime?: string
}
/**
 * 把任意来源的时间值统一转成 moment，供 DatePicker 使用。需要兼容三类来源：
 * 1) 用户交互产生的 moment，原样返回；
 * 2) 后端字段值 / 关联字段计算值，形如 "2026-09-01"，按配置 format 解析；
 * 3) 服务端暂存草稿：moment 经 JSON 序列化后变成 ISO 字符串（"2026-08-31T16:00:00.000Z"），
 *    必须按 ISO 解析回本地时区；若仍用 format 解析会截断时区信息，导致日期整体偏移一天。
 * 解析失败返回 undefined，避免把非 moment 值交给 DatePicker
 * （rc-picker 内部会调用 value.clone()，字符串会直接抛 TypeError 白屏）。
 */
const toMomentValue = (value: any, format: string): Moment | undefined => {
  if (value === undefined || value === null || value === '') return undefined
  if (moment.isMoment(value)) return value
  const text = String(value)
  const parsed = /^\d{4}-\d{2}-\d{2}T/.test(text)
    ? moment(text)
    : moment(text, format)
  return parsed.isValid() ? parsed : undefined
}

const reportingTypeMap = {
  NEW: '1',
  MODIFY: '2',
  SHELF_MODIFY: '3',
  PRICE_MODIFY: '4',
  RELAUNCHED: '5',
}

const ProjectValidationInfo = ({
  platformCode,
  readOnly = false,
  reportingType,
  form,
  onConfigChange,
  reportFieldValues,
}: Props) => {
  const [loading, setLoading] = useState(false)
  const [configResult, setConfigResult] =
    useState<ReportFieldConfigResult | null>(null)

  // 用 ref 保持 reportFieldValues 最新引用，确保异步函数可读到最新值
  const reportFieldValuesRef = useRef(reportFieldValues)
  reportFieldValuesRef.current = reportFieldValues

  // 拉取动态字段配置，并把后端 fieldValue 回填到表单
  // 说明：
  // 1) 该接口返回的是“字段配置 + 默认值”，页面需要先拿配置再决定渲染哪些控件；
  // 2) 由于不同控件的值类型不同（如多选是数组、时间是 moment），这里做统一的入参转换；
  // 3) 该函数只负责“取配置 + 首次回填”，不做业务提交，避免职责耦合。
  const loadReportFieldConfigs = async (
    code: string,
    reportingType?: string,
  ) => {
    setLoading(true)
    try {
      const res = await GET_REPORT_FIELD_CONFIGS({
        platformCode: code,
        reportingType:
          reportingTypeMap[reportingType as keyof typeof reportingTypeMap],
      })
      const data: ReportFieldConfigResult | null =
        ((res as any)?.data?.data as ReportFieldConfigResult) ||
        ((res as any)?.data as ReportFieldConfigResult) ||
        null
      // 注意：必须先算好 initialValues 并写回表单，最后才 setConfigResult 渲染控件。
      // React 17 在 await 之后的回调里不做批量更新，setConfigResult 会同步触发一次渲染；
      // 若此时表单里还残留暂存草稿反序列化出来的时间字符串，DatePicker 拿到非 moment 值就会白屏。

      // 将详情接口返回的已保存值（reportFieldValues）按 fieldKey / configId 建立映射，
      // 后续遍历字段配置时，优先使用已保存值覆盖配置默认值，确保编辑/详情场景回显正确。
      const savedValuesMap = new Map<string, string>()
      const savedValues = reportFieldValuesRef.current || []
      savedValues.forEach((dto) => {
        if (dto.fieldKey)
          savedValuesMap.set(`key_${dto.fieldKey}`, dto.fieldValue ?? '')
        if (dto.configId)
          savedValuesMap.set(`id_${dto.configId}`, dto.fieldValue ?? '')
      })

      const initialValues: Record<string, any> = {}
      data?.fieldKeyAndValue?.forEach((field: ReportFieldConfigVO) => {
        if (!field.fieldKey) return
        const formKey = `reportField_${field.fieldKey}`
        const currentValue = form.getFieldValue(formKey)
        const hasCurrentValue = Array.isArray(currentValue)
          ? currentValue.length > 0
          : currentValue !== undefined &&
            currentValue !== null &&
            currentValue !== ''

        // 若表单已有值（例如暂存恢复值），优先保留，避免被接口默认值覆盖
        if (hasCurrentValue) {
          if (field.controlType === controlTypeMap.TIME) {
            const timeRule = parseControlRule<TimeRule>(field)
            initialValues[formKey] = toMomentValue(
              currentValue,
              timeRule?.format || 'YYYY-MM-DD',
            )
          } else {
            initialValues[formKey] = currentValue
          }
          return
        }

        // 取值优先级：已保存值（reportFieldValues）> 配置默认值（fieldValue）
        const savedValue =
          savedValuesMap.get(`key_${field.fieldKey}`) ??
          (field.id ? savedValuesMap.get(`id_${field.id}`) : undefined)
        const effectiveValue =
          savedValue !== undefined ? savedValue : field.fieldValue

        if (field.controlType === controlTypeMap.MULTI_SELECT) {
          // 多选在后端通常是逗号分隔字符串，前端回填为数组
          // 示例： "A,B,C" -> ["A","B","C"]
          initialValues[formKey] = effectiveValue
            ? String(effectiveValue).split(',').filter(Boolean)
            : undefined
        } else if (field.controlType === controlTypeMap.TIME) {
          // 时间字段统一转换为 moment，保证 DatePicker 可正确显示
          // 注意：格式来自 controlRuleJson.format，默认 YYYY-MM-DD
          const timeRule = parseControlRule<TimeRule>(field)
          initialValues[formKey] = toMomentValue(
            effectiveValue,
            timeRule?.format || 'YYYY-MM-DD',
          )
        } else {
          initialValues[formKey] = effectiveValue
        }
      })
      form.setFieldsValue(initialValues)
      setConfigResult(data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!platformCode) {
      const fieldNames = fieldList
        .filter((f) => f.fieldKey)
        .map((f) => `reportField_${f.fieldKey}`)
      setConfigResult(null)
      if (fieldNames.length) form.resetFields(fieldNames)
      onConfigChange?.([])
      return
    }
    // platformCode 切换时重新获取该项目的字段配置
    loadReportFieldConfigs(platformCode, reportingType)
  }, [platformCode, reportingType])

  const fieldList = useMemo(
    () => configResult?.fieldKeyAndValue || [],
    [configResult],
  )

  useEffect(() => {
    onConfigChange?.(fieldList)
  }, [fieldList, onConfigChange])

  const parseOptions = (field: ReportFieldConfigVO) => {
    if (field.fieldEnumValueList?.length) {
      return field.fieldEnumValueList
    }
    try {
      const controlRule = field.controlRuleJson
        ? JSON.parse(field.controlRuleJson)
        : null
      if (Array.isArray(controlRule?.options)) {
        return controlRule.options
      }
    } catch (error) {
      return []
    }
    return []
  }

  // 解析控件规则 JSON。
  // 后端规则字段是字符串（controlRuleJson），这里反序列化为对应控件规则对象：
  // - NUMBER -> NumberRule
  // - TEXT   -> TextRule
  // - AMOUNT -> AmountRule
  // - TIME   -> TimeRule
  // - ENUM   -> EnumRule
  // 若 JSON 非法，返回 null 并走兜底逻辑（不阻塞页面渲染）。
  const parseControlRule = <T extends object>(
    field: ReportFieldConfigVO,
  ): T | null => {
    try {
      // controlRuleJson 是字符串，按控件类型反序列化为对应规则对象
      return field.controlRuleJson
        ? (JSON.parse(field.controlRuleJson) as T)
        : null
    } catch (error) {
      return null
    }
  }

  // 通用比较器：把文档中的 validationRules（>,>=,<,<=,=）统一映射到运行时判断。
  // 用在数字/金额控件，避免每个控件重复写比较分支。
  const checkByOperator = (input: number, rule: ValidationRule) => {
    const target = Number(rule.value)
    if (Number.isNaN(target) || !rule.operator) return true
    switch (rule.operator) {
      case '>':
        return input > target
      case '>=':
        return input >= target
      case '<':
        return input < target
      case '<=':
        return input <= target
      case '=':
        return input === target
      default:
        return true
    }
  }

  // 按时间格式生成“字符串形态校验”正则。
  // 这里先保证输入形态正确，再做 start/end 范围比较，避免无效日期格式直接参与比较。
  const getRegexByDateFormat = (format?: TimeRule['format']) => {
    if (format === 'YYYY') return /^\d{4}$/
    if (format === 'YYYY-MM') return /^\d{4}-(0[1-9]|1[0-2])$/
    return /^\d{4}-(0[1-9]|1[0-2])-([0][1-9]|[1-2][0-9]|3[0-1])$/
  }

  // 根据 format 映射 DatePicker 的 picker 模式（年 / 年月 / 日期）。
  // 注意：这不是“切换时间组件类型”，而是同一个 DatePicker 的展示粒度切换。
  const getPickerTypeByFormat = (format?: TimeRule['format']) => {
    if (format === 'YYYY') return 'year' as const
    if (format === 'YYYY-MM') return 'month' as const
    return 'date' as const
  }

  const getRequiredMessage = (field: ReportFieldConfigVO) => {
    if (field.controlType === controlTypeMap.SINGLE_SELECT) {
      return field.customErrorMsg || '请选择单个枚举值'
    }
    if (field.controlType === controlTypeMap.MULTI_SELECT) {
      return field.customErrorMsg || '请选择多个枚举值'
    }
    if (field.controlType === controlTypeMap.TIME) {
      return field.customErrorMsg || `请选择${field.fieldName}`
    }
    return field.customErrorMsg || `请输入${field.fieldName}`
  }

  const getLabelNode = (field: ReportFieldConfigVO) => {
    if (field.description && field.description.length > 20) {
      return (
        <Tooltip
          title={field.description}
          getPopupContainer={() => document.body}
        >
          {field.fieldName}
        </Tooltip>
      )
    }
    return field.fieldName
  }

  const renderField = (field: ReportFieldConfigVO) => {
    if (!field.fieldKey) return null
    const name = `reportField_${field.fieldKey}`
    const controlRule = parseControlRule<Record<string, any>>(field)
    const textRule =
      field.controlType === controlTypeMap.TEXT
        ? (controlRule as TextRule)
        : null
    const numberRule =
      field.controlType === controlTypeMap.NUMBER
        ? (controlRule as NumberRule)
        : null
    const amountRule =
      field.controlType === controlTypeMap.AMOUNT
        ? (controlRule as AmountRule)
        : null
    const timeRule =
      field.controlType === controlTypeMap.TIME
        ? (controlRule as TimeRule)
        : null

    // hasRelateField === 1 时，该字段由接口计算回填，用户不可编辑
    const isDisabled = readOnly || field.hasRelateField === 1

    // 规则组装策略（核心）：
    // 1) 先拼基础必填；
    // 2) 再按 controlType 叠加各自的 controlRuleJson 细粒度规则；
    // 3) 最后叠加 fieldLength 通用长度限制（作为兜底，避免超长数据进入表单）。
    const rules = [
      {
        required: field.hasRelateField === 1 ? false : !!field.required,
        ...(field.controlType === controlTypeMap.MULTI_SELECT
          ? { type: 'array' as const }
          : {}),
        message: getRequiredMessage(field),
      },
      ...(field.controlType === controlTypeMap.TEXT
        ? [
            {
              validator: async (_: any, value: any) => {
                if (value === undefined || value === null || value === '')
                  return
                const text = String(value)
                const min = textRule?.minLength
                const max = textRule?.maxLength
                // 文本长度范围校验
                if (typeof min === 'number' && text.length < min) {
                  throw new Error(field.customErrorMsg || `至少输入${min}位`)
                }
                if (typeof max === 'number' && text.length > max) {
                  throw new Error(field.customErrorMsg || `最多输入${max}位`)
                }
                const charTypes = textRule?.charTypes || []
                // 文本字符集校验：
                // 文档里 charTypes 支持：中文/英文/数字/符号
                if (charTypes.length) {
                  const regMap: Record<string, RegExp> = {
                    中文: /^[\u4e00-\u9fa5]$/,
                    英文: /^[A-Za-z]$/,
                    数字: /^[0-9]$/,
                    符号: /^[^A-Za-z0-9\u4e00-\u9fa5]$/,
                  }
                  // 逐字符校验：每个字符都必须命中至少一种允许的字符类型
                  const allCharsValid = Array.from(text).every((char) =>
                    charTypes.some((type) => regMap[type].test(char)),
                  )
                  if (!allCharsValid) {
                    const allowedTypes = charTypes.join('、')
                    const lenHints: string[] = []
                    if (typeof min === 'number') {
                      lenHints.push(`最小输入${min}个字符`)
                    }
                    if (typeof max === 'number') {
                      lenHints.push(`最大输入${max}个字符`)
                    }
                    const lengthSuffix =
                      lenHints.length > 0 ? `；长度${lenHints.join('，')}` : ''
                    throw new Error(
                      field.customErrorMsg ||
                        `请输入符合${allowedTypes}${lengthSuffix}`,
                    )
                  }
                }
              },
            },
          ]
        : []),
      ...(field.controlType === controlTypeMap.NUMBER
        ? [
            {
              validator: async (_: any, value: any) => {
                if (value === undefined || value === null || value === '')
                  return
                const text = String(value)
                const current = Number(value)
                if (Number.isNaN(current)) {
                  throw new Error(field.customErrorMsg || '请输入有效数字')
                }
                // 数字控件可配置 integer / decimal
                if (
                  numberRule?.numberType === 'integer' &&
                  !/^-?\d+$/.test(text)
                ) {
                  throw new Error(field.customErrorMsg || '请输入整数')
                }
                if (
                  numberRule?.numberType === 'decimal' &&
                  typeof numberRule.decimalDigits === 'number'
                ) {
                  const decimalPart = text.split('.')[1] || ''
                  if (decimalPart.length > numberRule.decimalDigits) {
                    throw new Error(
                      field.customErrorMsg ||
                        `最多保留${numberRule.decimalDigits}位小数`,
                    )
                  }
                }
                // 运算符规则校验（示例一 validationRules）
                const validateRules = numberRule?.validationRules || []
                const pass = validateRules.every((rule) =>
                  checkByOperator(current, rule),
                )
                if (!pass) {
                  const rangeHint = formatValidationRangeHint(validateRules)
                  throw new Error(
                    field.customErrorMsg || rangeHint || '数值不满足校验规则',
                  )
                }
              },
            },
          ]
        : []),
      ...(field.controlType === controlTypeMap.AMOUNT
        ? [
            {
              validator: async (_: any, value: any) => {
                if (value === undefined || value === null || value === '')
                  return
                const text = String(value)
                const current = Number(value)
                if (Number.isNaN(current)) {
                  throw new Error(field.customErrorMsg || '请输入有效金额')
                }
                if (typeof amountRule?.decimalDigits === 'number') {
                  const decimalPart = text.split('.')[1] || ''
                  if (decimalPart.length > amountRule.decimalDigits) {
                    throw new Error(
                      field.customErrorMsg ||
                        `最多保留${amountRule.decimalDigits}位小数`,
                    )
                  }
                }
                // 金额运算符规则校验（示例三 validationRules）
                const validateRules = amountRule?.validationRules || []
                const pass = validateRules.every((rule) =>
                  checkByOperator(current, rule),
                )
                if (!pass) {
                  const rangeHint = formatValidationRangeHint(validateRules)
                  const decSuffix =
                    typeof amountRule?.decimalDigits === 'number'
                      ? `，支持保留${amountRule.decimalDigits}位小数`
                      : ''
                  const defaultAmountMsg =
                    (rangeHint || '金额不满足校验规则') + decSuffix
                  throw new Error(field.customErrorMsg || defaultAmountMsg)
                }
              },
            },
          ]
        : []),
      ...(field.controlType === controlTypeMap.TIME
        ? [
            {
              validator: async (_: any, value: any) => {
                if (value === undefined || value === null || value === '')
                  return
                const format = timeRule?.format || 'YYYY-MM-DD'
                const text = moment.isMoment(value)
                  ? value.format(format)
                  : String(value)
                const reg = getRegexByDateFormat(format)
                if (!reg.test(text)) {
                  throw new Error(
                    field.customErrorMsg || `时间格式应为${format}`,
                  )
                }
                // 时间范围校验：startTime <= value <= endTime
                // 这里用同格式字符串做字典序比较，前提是格式是 YYYY / YYYY-MM / YYYY-MM-DD
                if (timeRule?.startTime && text < timeRule.startTime) {
                  throw new Error(
                    field.customErrorMsg || `不能早于${timeRule.startTime}`,
                  )
                }
                if (timeRule?.endTime && text > timeRule.endTime) {
                  throw new Error(
                    field.customErrorMsg || `不能晚于${timeRule.endTime}`,
                  )
                }
              },
            },
          ]
        : []),
      ...(field.fieldLength
        ? [
            {
              validator: async (_: any, value: any) => {
                if (value === undefined || value === null || value === '')
                  return
                if (String(value).length > field.fieldLength!) {
                  throw new Error(
                    field.customErrorMsg || `最多输入${field.fieldLength}位`,
                  )
                }
              },
            },
          ]
        : []),
    ]

    switch (field.controlType) {
      case controlTypeMap.NUMBER:
        return (
          <Col span={8} key={name}>
            <Form.Item name={name} label={getLabelNode(field)} rules={rules}>
              <InputNumber
                style={{ width: '100%' }}
                // 数字控件展示限制：
                // integer 固定 0 位小数；decimal 按 decimalDigits 限制可输入小数位
                precision={
                  numberRule?.numberType === 'decimal'
                    ? (numberRule.decimalDigits ?? undefined)
                    : 0
                }
                disabled={isDisabled}
                placeholder={field.description}
              />
            </Form.Item>
          </Col>
        )
      case controlTypeMap.AMOUNT:
        return (
          <Col span={8} key={name}>
            <Form.Item name={name} label={getLabelNode(field)} rules={rules}>
              <InputNumber
                style={{ width: '100%' }}
                // 金额控件展示限制：小数位由 amountRule.decimalDigits 驱动
                precision={amountRule?.decimalDigits ?? 2}
                disabled={isDisabled}
                placeholder={field.description}
              />
            </Form.Item>
          </Col>
        )
      case controlTypeMap.SINGLE_SELECT:
        return (
          <Col span={8} key={name}>
            <Form.Item name={name} label={getLabelNode(field)} rules={rules}>
              <Select
                disabled={readOnly}
                placeholder={field.description || '请选择'}
                options={parseOptions(field)}
              />
            </Form.Item>
          </Col>
        )
      case controlTypeMap.MULTI_SELECT:
        return (
          <Col span={8} key={name}>
            <Form.Item name={name} label={getLabelNode(field)} rules={rules}>
              <Select
                mode="multiple"
                disabled={readOnly}
                placeholder={field.description || '请选择'}
                options={parseOptions(field)}
              />
            </Form.Item>
          </Col>
        )
      case controlTypeMap.TIME:
        const timeFormat = timeRule?.format || 'YYYY-MM-DD'
        const startMoment = timeRule?.startTime
          ? moment(timeRule.startTime, timeFormat)
          : null
        const endMoment = timeRule?.endTime
          ? moment(timeRule.endTime, timeFormat)
          : null
        const pickerType = getPickerTypeByFormat(timeFormat)
        const compareUnit =
          pickerType === 'year'
            ? 'year'
            : pickerType === 'month'
              ? 'month'
              : 'day'
        return (
          <Col span={8} key={name}>
            <Form.Item
              name={name}
              label={getLabelNode(field)}
              rules={rules}
              // 兜底：表单值可能来自暂存草稿反序列化、关联字段计算回填等非 moment 来源，
              // 这里统一转换后再交给 DatePicker，避免 rc-picker 调用 value.clone() 崩溃
              getValueProps={(value: any) => ({
                value: toMomentValue(value, timeFormat),
              })}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabled={readOnly}
                placeholder={field.description || '请选择'}
                format={timeFormat}
                picker={pickerType}
                disabledDate={(current: Moment) => {
                  if (!current) return false
                  const tooEarly =
                    !!startMoment && current.isBefore(startMoment, compareUnit)
                  const tooLate =
                    !!endMoment && current.isAfter(endMoment, compareUnit)
                  return tooEarly || tooLate
                }}
              />
            </Form.Item>
          </Col>
        )
      case controlTypeMap.TEXT:
      default:
        return (
          <Col span={8} key={name}>
            <Form.Item name={name} label={getLabelNode(field)} rules={rules}>
              <Input
                disabled={isDisabled}
                placeholder={field.description}
                maxLength={
                  textRule?.maxLength || field.fieldLength || undefined
                }
              />
            </Form.Item>
          </Col>
        )
    }
  }

  if (!platformCode) {
    // return <Empty description="暂无可校验项目信息" />
    return <></>
  }

  return (
    // <Spin spinning={loading}>
    //   {fieldList.length ? (
    //     <Form
    //       {...formItemLayout}
    //       form={form as FormInstance}
    //       colon={false}
    //       labelAlign="right"
    //       style={{ margin: '10px 0 0 0', padding: '0 10px 0 10px' }}
    //     >
    //       <Row gutter={16}>{fieldList.map((field) => renderField(field))}</Row>
    //     </Form>
    //   ) : (
    //     <Empty description="当前项目暂无动态校验字段" />
    //   )}
    // </Spin>
    <>{fieldList.map((field) => renderField(field))}</>
  )
}

export default ProjectValidationInfo
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Tooltip,
} from 'antd'
import { FormInstance } from 'antd/lib/form'
import moment, { Moment } from 'moment'
import {
  GET_REPORT_FIELD_CONFIGS,
  ReportFieldConfigVO,
  ReportFieldConfigResult,
  ReportFieldValueSubmitDTO,
} from '@/api/reportApi'

interface Props {
  platformCode?: string
  readOnly?: boolean
  reportingType?: string
  form: FormInstance
  onConfigChange?: (fields: ReportFieldConfigVO[]) => void
  /** 物料详情/提报详情接口返回的已保存字段值，用于回显 */
  reportFieldValues?: ReportFieldValueSubmitDTO[]
}

const formItemLayout = {
  labelCol: { span: 10 },
  wrapperCol: { span: 14 },
}

const controlTypeMap = {
  NUMBER: '1',
  TEXT: '2',
  AMOUNT: '3',
  TIME: '4',
  SINGLE_SELECT: '5',
  MULTI_SELECT: '6',
} as const

type ValidationOperator = '>' | '>=' | '<' | '<=' | '='
type ValidationRule = { operator?: ValidationOperator; value?: string }

/** 根据 validationRules 拼默认提示：有下界才出现「大于…」，有上界才出现「小于…」；仅有 = 等其它规则时返回 null */
const formatValidationRangeHint = (rules: ValidationRule[]) => {
  const lowerParts: string[] = []
  const upperParts: string[] = []
  for (const rule of rules) {
    const v = Number(rule.value)
    if (Number.isNaN(v) || !rule.operator) continue
    if (rule.operator === '>') lowerParts.push(`大于${v}`)
    else if (rule.operator === '>=') lowerParts.push(`大于等于${v}`)
    else if (rule.operator === '<') upperParts.push(`小于${v}`)
    else if (rule.operator === '<=') upperParts.push(`小于等于${v}`)
  }
  if (!lowerParts.length && !upperParts.length) return null
  const minText = lowerParts.length ? lowerParts.join('、') : ''
  const maxText = upperParts.length ? upperParts.join('、') : ''
  if (minText && maxText) return `输入请符合${minText}，${maxText}`
  if (minText) return `输入请符合${minText}`
  if (maxText) return `输入请符合${maxText}`
  return null
}
type NumberRule = {
  numberType?: 'integer' | 'decimal'
  decimalDigits?: number
  validationRules?: ValidationRule[]
}
type TextRule = {
  charTypes?: Array<'中文' | '英文' | '数字' | '符号'>
  minLength?: number
  maxLength?: number
}
type AmountRule = { decimalDigits?: number; validationRules?: ValidationRule[] }
type TimeRule = {
  format?: 'YYYY' | 'YYYY-MM' | 'YYYY-MM-DD'
  startTime?: string
  endTime?: string
}
/**
 * 把任意来源的时间值统一转成 moment，供 DatePicker 使用。需要兼容三类来源：
 * 1) 用户交互产生的 moment，原样返回；
 * 2) 后端字段值 / 关联字段计算值，形如 "2026-09-01"，按配置 format 解析；
 * 3) 服务端暂存草稿：moment 经 JSON 序列化后变成 ISO 字符串（"2026-08-31T16:00:00.000Z"），
 *    必须按 ISO 解析回本地时区；若仍用 format 解析会截断时区信息，导致日期整体偏移一天。
 * 解析失败返回 undefined，避免把非 moment 值交给 DatePicker
 * （rc-picker 内部会调用 value.clone()，字符串会直接抛 TypeError 白屏）。
 */
const toMomentValue = (value: any, format: string): Moment | undefined => {
  if (value === undefined || value === null || value === '') return undefined
  if (moment.isMoment(value)) return value
  const text = String(value)
  const parsed = /^\d{4}-\d{2}-\d{2}T/.test(text)
    ? moment(text)
    : moment(text, format)
  return parsed.isValid() ? parsed : undefined
}

const reportingTypeMap = {
  NEW: '1',
  MODIFY: '2',
  SHELF_MODIFY: '3',
  PRICE_MODIFY: '4',
  RELAUNCHED: '5',
}

const ProjectValidationInfo = ({
  platformCode,
  readOnly = false,
  reportingType,
  form,
  onConfigChange,
  reportFieldValues,
}: Props) => {
  const [loading, setLoading] = useState(false)
  const [configResult, setConfigResult] =
    useState<ReportFieldConfigResult | null>(null)

  // 用 ref 保持 reportFieldValues 最新引用，确保异步函数可读到最新值
  const reportFieldValuesRef = useRef(reportFieldValues)
  reportFieldValuesRef.current = reportFieldValues

  // 拉取动态字段配置，并把后端 fieldValue 回填到表单
  // 说明：
  // 1) 该接口返回的是“字段配置 + 默认值”，页面需要先拿配置再决定渲染哪些控件；
  // 2) 由于不同控件的值类型不同（如多选是数组、时间是 moment），这里做统一的入参转换；
  // 3) 该函数只负责“取配置 + 首次回填”，不做业务提交，避免职责耦合。
  const loadReportFieldConfigs = async (
    code: string,
    reportingType?: string,
  ) => {
    setLoading(true)
    try {
      const res = await GET_REPORT_FIELD_CONFIGS({
        platformCode: code,
        reportingType:
          reportingTypeMap[reportingType as keyof typeof reportingTypeMap],
      })
      const data: ReportFieldConfigResult | null =
        ((res as any)?.data?.data as ReportFieldConfigResult) ||
        ((res as any)?.data as ReportFieldConfigResult) ||
        null
      // 注意：必须先算好 initialValues 并写回表单，最后才 setConfigResult 渲染控件。
      // React 17 在 await 之后的回调里不做批量更新，setConfigResult 会同步触发一次渲染；
      // 若此时表单里还残留暂存草稿反序列化出来的时间字符串，DatePicker 拿到非 moment 值就会白屏。

      // 将详情接口返回的已保存值（reportFieldValues）按 fieldKey / configId 建立映射，
      // 后续遍历字段配置时，优先使用已保存值覆盖配置默认值，确保编辑/详情场景回显正确。
      const savedValuesMap = new Map<string, string>()
      const savedValues = reportFieldValuesRef.current || []
      savedValues.forEach((dto) => {
        if (dto.fieldKey)
          savedValuesMap.set(`key_${dto.fieldKey}`, dto.fieldValue ?? '')
        if (dto.configId)
          savedValuesMap.set(`id_${dto.configId}`, dto.fieldValue ?? '')
      })

      const initialValues: Record<string, any> = {}
      data?.fieldKeyAndValue?.forEach((field: ReportFieldConfigVO) => {
        if (!field.fieldKey) return
        const formKey = `reportField_${field.fieldKey}`
        const currentValue = form.getFieldValue(formKey)
        const hasCurrentValue = Array.isArray(currentValue)
          ? currentValue.length > 0
          : currentValue !== undefined &&
            currentValue !== null &&
            currentValue !== ''

        // 若表单已有值（例如暂存恢复值），优先保留，避免被接口默认值覆盖
        if (hasCurrentValue) {
          if (field.controlType === controlTypeMap.TIME) {
            const timeRule = parseControlRule<TimeRule>(field)
            initialValues[formKey] = toMomentValue(
              currentValue,
              timeRule?.format || 'YYYY-MM-DD',
            )
          } else {
            initialValues[formKey] = currentValue
          }
          return
        }

        // 取值优先级：已保存值（reportFieldValues）> 配置默认值（fieldValue）
        const savedValue =
          savedValuesMap.get(`key_${field.fieldKey}`) ??
          (field.id ? savedValuesMap.get(`id_${field.id}`) : undefined)
        const effectiveValue =
          savedValue !== undefined ? savedValue : field.fieldValue

        if (field.controlType === controlTypeMap.MULTI_SELECT) {
          // 多选在后端通常是逗号分隔字符串，前端回填为数组
          // 示例： "A,B,C" -> ["A","B","C"]
          initialValues[formKey] = effectiveValue
            ? String(effectiveValue).split(',').filter(Boolean)
            : undefined
        } else if (field.controlType === controlTypeMap.TIME) {
          // 时间字段统一转换为 moment，保证 DatePicker 可正确显示
          // 注意：格式来自 controlRuleJson.format，默认 YYYY-MM-DD
          const timeRule = parseControlRule<TimeRule>(field)
          initialValues[formKey] = toMomentValue(
            effectiveValue,
            timeRule?.format || 'YYYY-MM-DD',
          )
        } else {
          initialValues[formKey] = effectiveValue
        }
      })
      form.setFieldsValue(initialValues)
      setConfigResult(data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!platformCode) {
      const fieldNames = fieldList
        .filter((f) => f.fieldKey)
        .map((f) => `reportField_${f.fieldKey}`)
      setConfigResult(null)
      if (fieldNames.length) form.resetFields(fieldNames)
      onConfigChange?.([])
      return
    }
    // platformCode 切换时重新获取该项目的字段配置
    loadReportFieldConfigs(platformCode, reportingType)
  }, [platformCode, reportingType])

  const fieldList = useMemo(
    () => configResult?.fieldKeyAndValue || [],
    [configResult],
  )

  useEffect(() => {
    onConfigChange?.(fieldList)
  }, [fieldList, onConfigChange])

  const parseOptions = (field: ReportFieldConfigVO) => {
    if (field.fieldEnumValueList?.length) {
      return field.fieldEnumValueList
    }
    try {
      const controlRule = field.controlRuleJson
        ? JSON.parse(field.controlRuleJson)
        : null
      if (Array.isArray(controlRule?.options)) {
        return controlRule.options
      }
    } catch (error) {
      return []
    }
    return []
  }

  // 解析控件规则 JSON。
  // 后端规则字段是字符串（controlRuleJson），这里反序列化为对应控件规则对象：
  // - NUMBER -> NumberRule
  // - TEXT   -> TextRule
  // - AMOUNT -> AmountRule
  // - TIME   -> TimeRule
  // - ENUM   -> EnumRule
  // 若 JSON 非法，返回 null 并走兜底逻辑（不阻塞页面渲染）。
  const parseControlRule = <T extends object>(
    field: ReportFieldConfigVO,
  ): T | null => {
    try {
      // controlRuleJson 是字符串，按控件类型反序列化为对应规则对象
      return field.controlRuleJson
        ? (JSON.parse(field.controlRuleJson) as T)
        : null
    } catch (error) {
      return null
    }
  }

  // 通用比较器：把文档中的 validationRules（>,>=,<,<=,=）统一映射到运行时判断。
  // 用在数字/金额控件，避免每个控件重复写比较分支。
  const checkByOperator = (input: number, rule: ValidationRule) => {
    const target = Number(rule.value)
    if (Number.isNaN(target) || !rule.operator) return true
    switch (rule.operator) {
      case '>':
        return input > target
      case '>=':
        return input >= target
      case '<':
        return input < target
      case '<=':
        return input <= target
      case '=':
        return input === target
      default:
        return true
    }
  }

  // 按时间格式生成“字符串形态校验”正则。
  // 这里先保证输入形态正确，再做 start/end 范围比较，避免无效日期格式直接参与比较。
  const getRegexByDateFormat = (format?: TimeRule['format']) => {
    if (format === 'YYYY') return /^\d{4}$/
    if (format === 'YYYY-MM') return /^\d{4}-(0[1-9]|1[0-2])$/
    return /^\d{4}-(0[1-9]|1[0-2])-([0][1-9]|[1-2][0-9]|3[0-1])$/
  }

  // 根据 format 映射 DatePicker 的 picker 模式（年 / 年月 / 日期）。
  // 注意：这不是“切换时间组件类型”，而是同一个 DatePicker 的展示粒度切换。
  const getPickerTypeByFormat = (format?: TimeRule['format']) => {
    if (format === 'YYYY') return 'year' as const
    if (format === 'YYYY-MM') return 'month' as const
    return 'date' as const
  }

  const getRequiredMessage = (field: ReportFieldConfigVO) => {
    if (field.controlType === controlTypeMap.SINGLE_SELECT) {
      return field.customErrorMsg || '请选择单个枚举值'
    }
    if (field.controlType === controlTypeMap.MULTI_SELECT) {
      return field.customErrorMsg || '请选择多个枚举值'
    }
    if (field.controlType === controlTypeMap.TIME) {
      return field.customErrorMsg || `请选择${field.fieldName}`
    }
    return field.customErrorMsg || `请输入${field.fieldName}`
  }

  const getLabelNode = (field: ReportFieldConfigVO) => {
    if (field.description && field.description.length > 20) {
      return (
        <Tooltip
          title={field.description}
          getPopupContainer={() => document.body}
        >
          {field.fieldName}
        </Tooltip>
      )
    }
    return field.fieldName
  }

  const renderField = (field: ReportFieldConfigVO) => {
    if (!field.fieldKey) return null
    const name = `reportField_${field.fieldKey}`
    const controlRule = parseControlRule<Record<string, any>>(field)
    const textRule =
      field.controlType === controlTypeMap.TEXT
        ? (controlRule as TextRule)
        : null
    const numberRule =
      field.controlType === controlTypeMap.NUMBER
        ? (controlRule as NumberRule)
        : null
    const amountRule =
      field.controlType === controlTypeMap.AMOUNT
        ? (controlRule as AmountRule)
        : null
    const timeRule =
      field.controlType === controlTypeMap.TIME
        ? (controlRule as TimeRule)
        : null

    // hasRelateField === 1 时，该字段由接口计算回填，用户不可编辑
    const isDisabled = readOnly || field.hasRelateField === 1

    // 规则组装策略（核心）：
    // 1) 先拼基础必填；
    // 2) 再按 controlType 叠加各自的 controlRuleJson 细粒度规则；
    // 3) 最后叠加 fieldLength 通用长度限制（作为兜底，避免超长数据进入表单）。
    const rules = [
      {
        required: field.hasRelateField === 1 ? false : !!field.required,
        ...(field.controlType === controlTypeMap.MULTI_SELECT
          ? { type: 'array' as const }
          : {}),
        message: getRequiredMessage(field),
      },
      ...(field.controlType === controlTypeMap.TEXT
        ? [
            {
              validator: async (_: any, value: any) => {
                if (value === undefined || value === null || value === '')
                  return
                const text = String(value)
                const min = textRule?.minLength
                const max = textRule?.maxLength
                // 文本长度范围校验
                if (typeof min === 'number' && text.length < min) {
                  throw new Error(field.customErrorMsg || `至少输入${min}位`)
                }
                if (typeof max === 'number' && text.length > max) {
                  throw new Error(field.customErrorMsg || `最多输入${max}位`)
                }
                const charTypes = textRule?.charTypes || []
                // 文本字符集校验：
                // 文档里 charTypes 支持：中文/英文/数字/符号
                if (charTypes.length) {
                  const regMap: Record<string, RegExp> = {
                    中文: /^[\u4e00-\u9fa5]$/,
                    英文: /^[A-Za-z]$/,
                    数字: /^[0-9]$/,
                    符号: /^[^A-Za-z0-9\u4e00-\u9fa5]$/,
                  }
                  // 逐字符校验：每个字符都必须命中至少一种允许的字符类型
                  const allCharsValid = Array.from(text).every((char) =>
                    charTypes.some((type) => regMap[type].test(char)),
                  )
                  if (!allCharsValid) {
                    const allowedTypes = charTypes.join('、')
                    const lenHints: string[] = []
                    if (typeof min === 'number') {
                      lenHints.push(`最小输入${min}个字符`)
                    }
                    if (typeof max === 'number') {
                      lenHints.push(`最大输入${max}个字符`)
                    }
                    const lengthSuffix =
                      lenHints.length > 0 ? `；长度${lenHints.join('，')}` : ''
                    throw new Error(
                      field.customErrorMsg ||
                        `请输入符合${allowedTypes}${lengthSuffix}`,
                    )
                  }
                }
              },
            },
          ]
        : []),
      ...(field.controlType === controlTypeMap.NUMBER
        ? [
            {
              validator: async (_: any, value: any) => {
                if (value === undefined || value === null || value === '')
                  return
                const text = String(value)
                const current = Number(value)
                if (Number.isNaN(current)) {
                  throw new Error(field.customErrorMsg || '请输入有效数字')
                }
                // 数字控件可配置 integer / decimal
                if (
                  numberRule?.numberType === 'integer' &&
                  !/^-?\d+$/.test(text)
                ) {
                  throw new Error(field.customErrorMsg || '请输入整数')
                }
                if (
                  numberRule?.numberType === 'decimal' &&
                  typeof numberRule.decimalDigits === 'number'
                ) {
                  const decimalPart = text.split('.')[1] || ''
                  if (decimalPart.length > numberRule.decimalDigits) {
                    throw new Error(
                      field.customErrorMsg ||
                        `最多保留${numberRule.decimalDigits}位小数`,
                    )
                  }
                }
                // 运算符规则校验（示例一 validationRules）
                const validateRules = numberRule?.validationRules || []
                const pass = validateRules.every((rule) =>
                  checkByOperator(current, rule),
                )
                if (!pass) {
                  const rangeHint = formatValidationRangeHint(validateRules)
                  throw new Error(
                    field.customErrorMsg || rangeHint || '数值不满足校验规则',
                  )
                }
              },
            },
          ]
        : []),
      ...(field.controlType === controlTypeMap.AMOUNT
        ? [
            {
              validator: async (_: any, value: any) => {
                if (value === undefined || value === null || value === '')
                  return
                const text = String(value)
                const current = Number(value)
                if (Number.isNaN(current)) {
                  throw new Error(field.customErrorMsg || '请输入有效金额')
                }
                if (typeof amountRule?.decimalDigits === 'number') {
                  const decimalPart = text.split('.')[1] || ''
                  if (decimalPart.length > amountRule.decimalDigits) {
                    throw new Error(
                      field.customErrorMsg ||
                        `最多保留${amountRule.decimalDigits}位小数`,
                    )
                  }
                }
                // 金额运算符规则校验（示例三 validationRules）
                const validateRules = amountRule?.validationRules || []
                const pass = validateRules.every((rule) =>
                  checkByOperator(current, rule),
                )
                if (!pass) {
                  const rangeHint = formatValidationRangeHint(validateRules)
                  const decSuffix =
                    typeof amountRule?.decimalDigits === 'number'
                      ? `，支持保留${amountRule.decimalDigits}位小数`
                      : ''
                  const defaultAmountMsg =
                    (rangeHint || '金额不满足校验规则') + decSuffix
                  throw new Error(field.customErrorMsg || defaultAmountMsg)
                }
              },
            },
          ]
        : []),
      ...(field.controlType === controlTypeMap.TIME
        ? [
            {
              validator: async (_: any, value: any) => {
                if (value === undefined || value === null || value === '')
                  return
                const format = timeRule?.format || 'YYYY-MM-DD'
                const text = moment.isMoment(value)
                  ? value.format(format)
                  : String(value)
                const reg = getRegexByDateFormat(format)
                if (!reg.test(text)) {
                  throw new Error(
                    field.customErrorMsg || `时间格式应为${format}`,
                  )
                }
                // 时间范围校验：startTime <= value <= endTime
                // 这里用同格式字符串做字典序比较，前提是格式是 YYYY / YYYY-MM / YYYY-MM-DD
                if (timeRule?.startTime && text < timeRule.startTime) {
                  throw new Error(
                    field.customErrorMsg || `不能早于${timeRule.startTime}`,
                  )
                }
                if (timeRule?.endTime && text > timeRule.endTime) {
                  throw new Error(
                    field.customErrorMsg || `不能晚于${timeRule.endTime}`,
                  )
                }
              },
            },
          ]
        : []),
      ...(field.fieldLength
        ? [
            {
              validator: async (_: any, value: any) => {
                if (value === undefined || value === null || value === '')
                  return
                if (String(value).length > field.fieldLength!) {
                  throw new Error(
                    field.customErrorMsg || `最多输入${field.fieldLength}位`,
                  )
                }
              },
            },
          ]
        : []),
    ]

    switch (field.controlType) {
      case controlTypeMap.NUMBER:
        return (
          <Col span={8} key={name}>
            <Form.Item name={name} label={getLabelNode(field)} rules={rules}>
              <InputNumber
                style={{ width: '100%' }}
                // 数字控件展示限制：
                // integer 固定 0 位小数；decimal 按 decimalDigits 限制可输入小数位
                precision={
                  numberRule?.numberType === 'decimal'
                    ? (numberRule.decimalDigits ?? undefined)
                    : 0
                }
                disabled={isDisabled}
                placeholder={field.description}
              />
            </Form.Item>
          </Col>
        )
      case controlTypeMap.AMOUNT:
        return (
          <Col span={8} key={name}>
            <Form.Item name={name} label={getLabelNode(field)} rules={rules}>
              <InputNumber
                style={{ width: '100%' }}
                // 金额控件展示限制：小数位由 amountRule.decimalDigits 驱动
                precision={amountRule?.decimalDigits ?? 2}
                disabled={isDisabled}
                placeholder={field.description}
              />
            </Form.Item>
          </Col>
        )
      case controlTypeMap.SINGLE_SELECT:
        return (
          <Col span={8} key={name}>
            <Form.Item name={name} label={getLabelNode(field)} rules={rules}>
              <Select
                disabled={readOnly}
                placeholder={field.description || '请选择'}
                options={parseOptions(field)}
              />
            </Form.Item>
          </Col>
        )
      case controlTypeMap.MULTI_SELECT:
        return (
          <Col span={8} key={name}>
            <Form.Item name={name} label={getLabelNode(field)} rules={rules}>
              <Select
                mode="multiple"
                disabled={readOnly}
                placeholder={field.description || '请选择'}
                options={parseOptions(field)}
              />
            </Form.Item>
          </Col>
        )
      case controlTypeMap.TIME:
        const timeFormat = timeRule?.format || 'YYYY-MM-DD'
        const startMoment = timeRule?.startTime
          ? moment(timeRule.startTime, timeFormat)
          : null
        const endMoment = timeRule?.endTime
          ? moment(timeRule.endTime, timeFormat)
          : null
        const pickerType = getPickerTypeByFormat(timeFormat)
        const compareUnit =
          pickerType === 'year'
            ? 'year'
            : pickerType === 'month'
              ? 'month'
              : 'day'
        return (
          <Col span={8} key={name}>
            <Form.Item
              name={name}
              label={getLabelNode(field)}
              rules={rules}
              // 兜底：表单值可能来自暂存草稿反序列化、关联字段计算回填等非 moment 来源，
              // 这里统一转换后再交给 DatePicker，避免 rc-picker 调用 value.clone() 崩溃
              getValueProps={(value: any) => ({
                value: toMomentValue(value, timeFormat),
              })}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabled={readOnly}
                placeholder={field.description || '请选择'}
                format={timeFormat}
                picker={pickerType}
                disabledDate={(current: Moment) => {
                  if (!current) return false
                  const tooEarly =
                    !!startMoment && current.isBefore(startMoment, compareUnit)
                  const tooLate =
                    !!endMoment && current.isAfter(endMoment, compareUnit)
                  return tooEarly || tooLate
                }}
              />
            </Form.Item>
          </Col>
        )
      case controlTypeMap.TEXT:
      default:
        return (
          <Col span={8} key={name}>
            <Form.Item name={name} label={getLabelNode(field)} rules={rules}>
              <Input
                disabled={isDisabled}
                placeholder={field.description}
                maxLength={
                  textRule?.maxLength || field.fieldLength || undefined
                }
              />
            </Form.Item>
          </Col>
        )
    }
  }

  if (!platformCode) {
    // return <Empty description="暂无可校验项目信息" />
    return <></>
  }

  return (
    // <Spin spinning={loading}>
    //   {fieldList.length ? (
    //     <Form
    //       {...formItemLayout}
    //       form={form as FormInstance}
    //       colon={false}
    //       labelAlign="right"
    //       style={{ margin: '10px 0 0 0', padding: '0 10px 0 10px' }}
    //     >
    //       <Row gutter={16}>{fieldList.map((field) => renderField(field))}</Row>
    //     </Form>
    //   ) : (
    //     <Empty description="当前项目暂无动态校验字段" />
    //   )}
    // </Spin>
    <>{fieldList.map((field) => renderField(field))}</>
  )
}

export default ProjectValidationInfo
